import { Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon, PhotoIcon, DocumentTextIcon, BuildingStorefrontIcon, ClipboardIcon, IdentificationIcon, EnvelopeIcon, PhoneIcon } from '@heroicons/react/24/outline'
import { colorMap, formatDate } from '../../Common'
import { toast } from 'react-toastify'

const ModalVisualizacaoContasRecebimento = ({ isOpen, onClose, conta, color = 'orange' }) => {
    const colorClasses = colorMap[color] || colorMap.orange

    if (!conta) return null

    const hasDadosBancarios = conta.banco || conta.agencia || conta.conta

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text)
        toast.success('Chave PIX copiada!')
    }

    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-white px-4 pb-6 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                                <div className="absolute right-0 top-0 pr-4 pt-4">
                                    <button
                                        type="button"
                                        className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-colors"
                                        onClick={onClose}
                                    >
                                        <span className="sr-only">Fechar</span>
                                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                    </button>
                                </div>

                                <div className="border-b border-gray-200 pb-4">
                                    <div className="inline-flex items-center justify-center w-full px-4 py-2 rounded-lg bg-gray-50 mb-3">
                                        <span className="text-sm font-semibold text-gray-700">
                                            👁️ Visualização
                                        </span>
                                    </div>
                                    <h3 className="text-2xl font-bold leading-7 text-center text-gray-900">
                                        Detalhes da Conta de Recebimento
                                    </h3>
                                    <p className="mt-2 text-sm text-gray-500 text-center">
                                        Informações completas da conta cadastrada
                                    </p>
                                </div>

                                <div className="mt-6 space-y-8">
                                    <div className="border-b border-gray-200 pb-8">
                                        <div className="flex items-center gap-3 mb-4">
                                            <PhotoIcon className="h-5 w-5 text-orange-600" />
                                            <h4 className="text-base font-semibold text-gray-900">QR Code PIX</h4>
                                        </div>
                                        <div className="mt-4 flex justify-center">
                                            {conta.qrcode_path ? (
                                                <div className="relative w-full max-w-xs">
                                                    <img
                                                        src={conta.qrcode_path.startsWith('data:')
                                                            ? conta.qrcode_path
                                                            : `data:image/webp;base64,${conta.qrcode_path}`}
                                                        alt="QR Code PIX"
                                                        className="h-40 w-40 sm:h-48 sm:w-48 mx-auto object-contain rounded-lg border-2 border-gray-200 bg-white p-2"
                                                    />
                                                </div>
                                            ) : (
                                                <div className="text-center text-gray-400 py-8">
                                                    <PhotoIcon className="w-12 h-12 mx-auto mb-2" />
                                                    <p className="text-sm">QR Code não disponível</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="border-b border-gray-200 pb-8">
                                        <div className="flex items-center gap-3 mb-4">
                                            <DocumentTextIcon className="h-5 w-5 text-orange-600" />
                                            <h4 className="text-base font-semibold text-gray-900">Informações da Conta</h4>
                                        </div>
                                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-500 flex items-center gap-1">
                                                    <DocumentTextIcon className="w-4 h-4" />
                                                    Nome da Empresa
                                                </label>
                                                <p className="mt-1 text-sm font-semibold text-gray-900">
                                                    {conta.nome_empresa}
                                                </p>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-500 flex items-center gap-1">
                                                    <IdentificationIcon className="w-4 h-4" />
                                                    CNPJ
                                                </label>
                                                <p className="mt-1 text-sm font-semibold text-gray-900">
                                                    {conta.cnpj || '-'}
                                                </p>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-500 flex items-center gap-1">
                                                    <EnvelopeIcon className="w-4 h-4" />
                                                    E-mail
                                                </label>
                                                <p className="mt-1 text-sm font-semibold text-gray-900 break-all">
                                                    {conta.email || '-'}
                                                </p>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-500 flex items-center gap-1">
                                                    <PhoneIcon className="w-4 h-4" />
                                                    Telefone
                                                </label>
                                                <p className="mt-1 text-sm font-semibold text-gray-900">
                                                    {conta.telefone || '-'}
                                                </p>
                                            </div>

                                            <div className="sm:col-span-1">
                                                <label className="block text-sm font-medium text-gray-500 flex items-center gap-1">
                                                    <ClipboardIcon className="w-4 h-4" />
                                                    Titular da Conta
                                                </label>
                                                <div className="mt-1 flex items-center gap-2">
                                                    <p className="text-sm font-semibold text-gray-900 break-all">
                                                        {conta.nome_titular}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="sm:col-span-1">
                                                <label className="block text-sm font-medium text-gray-500 flex items-center gap-1">
                                                    <ClipboardIcon className="w-4 h-4" />
                                                    Chave PIX
                                                </label>
                                                <div className="mt-1 flex items-center gap-2">
                                                    <p className="text-sm font-semibold text-gray-900 break-all">
                                                        {conta.chave_pix}
                                                    </p>
                                                    <button
                                                        onClick={() => copyToClipboard(conta.chave_pix)}
                                                        className="p-1 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
                                                        title="Copiar chave PIX"
                                                    >
                                                        <ClipboardIcon className="w-4 h-4 text-gray-600" />
                                                    </button>
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-500">
                                                    Data de Criação
                                                </label>
                                                <p className="mt-1 text-sm font-semibold text-gray-900">
                                                    {formatDate(conta.data_criacao)}
                                                </p>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-500">
                                                    Última Atualização
                                                </label>
                                                <p className="mt-1 text-sm font-semibold text-gray-900">
                                                    {formatDate(conta.data_atualizacao)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {hasDadosBancarios && (
                                        <div>
                                            <div className="flex items-center gap-3 mb-4">
                                                <BuildingStorefrontIcon className="h-5 w-5 text-orange-600" />
                                                <h4 className="text-base font-semibold text-gray-900">Dados Bancários</h4>
                                            </div>
                                            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-6">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-500">
                                                        Banco
                                                    </label>
                                                    <p className="mt-1 text-sm font-semibold text-gray-900">
                                                        {conta.banco || '-'}
                                                    </p>
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-500">
                                                        Agência
                                                    </label>
                                                    <p className="mt-1 text-sm font-semibold text-gray-900">
                                                        {conta.agencia || '-'}
                                                    </p>
                                                </div>

                                                <div className="sm:col-span-2">
                                                    <label className="block text-sm font-medium text-gray-500">
                                                        Conta
                                                    </label>
                                                    <p className="mt-1 text-sm font-semibold text-gray-900">
                                                        {conta.conta || '-'}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {!hasDadosBancarios && (
                                        <div className="bg-gray-50 rounded-lg p-4 text-center">
                                            <BuildingStorefrontIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                            <p className="text-sm text-gray-600">
                                                Esta conta não possui dados bancários cadastrados
                                            </p>
                                        </div>
                                    )}
                                </div>

                                <div className="mt-8 flex items-center justify-end border-t border-gray-200 pt-6">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className={`rounded-lg px-6 py-2.5 text-sm font-semibold text-white shadow-sm ${colorClasses.headerButtonBg} ${colorClasses.headerButtonHover} transition-all`}
                                    >
                                        Fechar
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}

export default ModalVisualizacaoContasRecebimento

